import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logAiAdvisoryEvent } from "@/lib/aiAdvisory";
import { sanitizeAiUserMessage } from "@/lib/aiUserFacingErrors";

export type WalkthroughKind = "unit" | "inventory" | "appliance";

export type WalkthroughRoomMark = { at_s: number; name: string };

export type WalkthroughAiItem = {
  name: string;
  kind: WalkthroughKind;
  room: string;
  spoken_text: string | null;
  spoken_number: number | null;
  spoken_level: string | null;
  spoken_is_vague: boolean;
  timestamp_s: number;
  model: string | null;
  serial?: string | null;
  count?: number;
  confidence?: number;
  is_model_plate?: boolean;
};

export class WalkthroughAnalyzeError extends Error {
  constructor(
    message: string,
    public readonly error_code?: string,
    public readonly request_id?: string,
  ) {
    super(message);
    this.name = "WalkthroughAnalyzeError";
  }
}

const BUCKET = "reference-photos";

export async function uploadWalkthroughVideo(
  file: Blob,
  propertyId: string,
): Promise<{ videoUrl: string; path: string; mimeType: string }> {
  const mime = file.type && file.type.startsWith("video/") ? file.type : "video/webm";
  const ext = mime.includes("mp4") ? "mp4" : mime.includes("quicktime") ? "mov" : "webm";
  const date = new Date().toISOString().slice(0, 10);
  const uuid = crypto.randomUUID().replace(/-/g, "").slice(0, 12);
  const path = `${propertyId}/inventory-walkthroughs/${date}/${uuid}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    upsert: true,
    contentType: mime,
  });
  if (error) throw new Error(error.message || "Upload failed");
  const { data: signed, error: signErr } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 30);
  if (signErr || !signed?.signedUrl) {
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
    return { videoUrl: data.publicUrl, path, mimeType: mime };
  }
  return { videoUrl: signed.signedUrl, path, mimeType: mime };
}

export async function uploadWalkthroughStill(
  blob: Blob,
  propertyId: string,
): Promise<string> {
  const date = new Date().toISOString().slice(0, 10);
  const uuid = crypto.randomUUID().replace(/-/g, "").slice(0, 12);
  const path = `${propertyId}/inventory-walkthroughs/${date}/still-${uuid}.jpg`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
    upsert: true,
    contentType: "image/jpeg",
  });
  if (error) throw new Error(error.message || "Still upload failed");
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  const { data: signed } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 60 * 24 * 365);
  return signed?.signedUrl || data.publicUrl;
}

export async function analyzeWalkthroughVideo(input: {
  propertyId: string;
  videoUrl: string;
  mimeType: string;
  duration_s: number;
  rooms: WalkthroughRoomMark[];
}): Promise<WalkthroughAiItem[]> {
  const t0 = performance.now();
  const { data, error } = await supabase.functions.invoke("analyze-inventory-walkthrough", {
    body: input,
  });
  try {
    if (error) {
      let message = error.message || "We couldn't read that video";
      let error_code: string | undefined;
      let request_id: string | undefined;
      if (error instanceof FunctionsHttpError && error.context) {
        try {
          const errBody = (await (error.context as Response).json()) as {
            message?: string;
            error_code?: string;
            request_id?: string;
          };
          message = errBody.message ?? message;
          error_code = errBody.error_code;
          request_id = errBody.request_id;
        } catch {
          /* keep */
        }
      }
      const status =
        error instanceof FunctionsHttpError && error.context
          ? (error.context as Response).status
          : undefined;
      throw new WalkthroughAnalyzeError(
        sanitizeAiUserMessage(message, error_code, status),
        error_code,
        request_id,
      );
    }
    const typed = data as { ok?: boolean; items?: WalkthroughAiItem[]; message?: string; error_code?: string };
    if (!typed || typed.ok !== true || !Array.isArray(typed.items)) {
      throw new WalkthroughAnalyzeError(
        sanitizeAiUserMessage(typed?.message ?? "We couldn't read that photo", typed?.error_code),
        typed?.error_code,
      );
    }
    logAiAdvisoryEvent({
      kind: "inventory_walkthrough",
      propertyId: input.propertyId,
      durationMs: performance.now() - t0,
      outcome: typed.items.length === 0 ? "empty" : "ok",
      extra: { itemCount: typed.items.length },
    });
    return typed.items.map((it) => ({
      ...it,
      kind: it.kind === "inventory" || it.kind === "appliance" ? it.kind : "unit",
      model: it.model ?? null,
    }));
  } catch (err) {
    logAiAdvisoryEvent({
      kind: "inventory_walkthrough",
      propertyId: input.propertyId,
      durationMs: performance.now() - t0,
      outcome: err instanceof WalkthroughAnalyzeError ? err.error_code ?? "error" : "error",
    });
    throw err;
  }
}

export async function analyzeWalkthroughItem(input: {
  propertyId: string;
  /** Prefer inline base64 — skips storage fetch on the edge. */
  imageBase64?: string;
  imageUrl?: string;
  room: string;
  mode?: "walkthrough_item" | "model_plate";
  attachHint?: string | null;
}): Promise<WalkthroughAiItem> {
  const t0 = performance.now();
  const hasInline = Boolean(input.imageBase64?.trim());
  const hasUrl = Boolean(input.imageUrl?.trim());
  if (!hasInline && !hasUrl) {
    throw new WalkthroughAnalyzeError("Missing image for identify");
  }
  const { data, error } = await supabase.functions.invoke("analyze-inventory-walkthrough", {
    body: {
      propertyId: input.propertyId,
      ...(hasInline
        ? { imageBase64: input.imageBase64, mimeType: "image/jpeg" }
        : { imageUrl: input.imageUrl, mimeType: "image/jpeg" }),
      room: input.room,
      mode: input.mode || "walkthrough_item",
      attachHint: input.attachHint || undefined,
    },
  });
  try {
    if (error) {
      let message = error.message || "We couldn't read that photo";
      let error_code: string | undefined;
      let request_id: string | undefined;
      let status: number | undefined;
      if (error instanceof FunctionsHttpError && error.context) {
        status = (error.context as Response).status;
        try {
          const errBody = (await (error.context as Response).json()) as {
            message?: string;
            error_code?: string;
            request_id?: string;
          };
          message = errBody.message ?? message;
          error_code = errBody.error_code;
          request_id = errBody.request_id;
        } catch {
          /* keep */
        }
      }
      throw new WalkthroughAnalyzeError(
        sanitizeAiUserMessage(message, error_code, status),
        error_code,
        request_id,
      );
    }
    const typed = data as {
      ok?: boolean;
      item?: WalkthroughAiItem;
      items?: WalkthroughAiItem[];
      message?: string;
      error_code?: string;
    };
    const item = typed?.item ?? typed?.items?.[0];
    if (!typed || typed.ok !== true || !item) {
      throw new WalkthroughAnalyzeError(
        sanitizeAiUserMessage(typed?.message ?? "We couldn't read that photo", typed?.error_code),
        typed?.error_code,
      );
    }
    logAiAdvisoryEvent({
      kind: "inventory_walkthrough",
      propertyId: input.propertyId,
      durationMs: performance.now() - t0,
      outcome: "ok",
      extra: { mode: input.mode || "walkthrough_item", inline: hasInline },
    });
    return {
      ...item,
      kind: item.kind === "inventory" || item.kind === "appliance" ? item.kind : "unit",
      model: item.model ?? null,
      serial: item.serial ?? null,
      count: item.count && item.count > 0 ? item.count : 1,
      confidence: item.confidence ?? 0.7,
      is_model_plate: Boolean(item.is_model_plate),
    };
  } catch (err) {
    logAiAdvisoryEvent({
      kind: "inventory_walkthrough",
      propertyId: input.propertyId,
      durationMs: performance.now() - t0,
      outcome: err instanceof WalkthroughAnalyzeError ? err.error_code ?? "error" : "error",
    });
    throw err;
  }
}

export async function captureVideoFrame(video: HTMLVideoElement, seconds: number): Promise<Blob | null> {
  const dur = Number.isFinite(video.duration) ? video.duration : seconds;
  const t = Math.max(0, Math.min(seconds, Math.max(0, dur - 0.05)));
  await new Promise<void>((resolve, reject) => {
    const onSeeked = () => {
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onErr);
      resolve();
    };
    const onErr = () => {
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onErr);
      reject(new Error("seek failed"));
    };
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("error", onErr);
    video.currentTime = t;
  });
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return null;
  const max = 960;
  const scale = Math.min(1, max / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.82);
  });
}
